// src/services/building3DService.js - Сервис для работы с 3D моделями зданий
import { mapiService } from './mapiService';

class Building3DService {
  constructor() {
    this.apiEndpoint = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    this.supportedFormats = ['glb', 'gltf', 'obj', 'fbx'];
  }

  // Получение дополнительных деталей здания
  async getBuildingDetails(lat, lng) {
    try {
      console.log('🏢 Getting building details for:', lat, lng);
      
      // Используем reverse geocoding для получения дополнительной информации
      const reverseData = await mapiService.reverseGeocode(lat, lng);
      
      if (reverseData && reverseData.features && reverseData.features.length > 0) {
        const feature = reverseData.features[0];
        const props = feature.properties || {};
        
        return {
          additional_data: {
            country: props.country || '',
            region: props.region || props.state || '',
            city: props.city || props.locality || '',
            district: props.neighbourhood || props.district || '',
            postcode: props.postcode || ''
          },
          source_quality: 'api_enhanced'
        };
      }
      
      return null;
      
    } catch (error) {
      console.warn('Could not fetch building details:', error);
      return null;
    }
  }

  // Расчет площади здания по геометрии
  calculateBuildingArea(geometry) {
    if (!geometry) return null;
    
    try {
      if (geometry.type === 'Polygon') {
        return this.calculatePolygonArea(geometry.coordinates[0]);
      } else if (geometry.type === 'MultiPolygon') {
        let totalArea = 0;
        geometry.coordinates.forEach(polygon => {
          totalArea += this.calculatePolygonArea(polygon[0]);
        });
        return totalArea;
      }
    } catch (error) {
      console.warn('Could not calculate building area:', error);
    }
    
    return null;
  }

  // Расчет площади полигона (приблизительный, в м²)
  calculatePolygonArea(coordinates) {
    if (!coordinates || coordinates.length < 3) return 0;
    
    // Используем формулу Shoelace для расчета площади
    let area = 0;
    const n = coordinates.length - 1; // последняя точка повторяет первую
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += coordinates[i][0] * coordinates[j][1];
      area -= coordinates[j][0] * coordinates[i][1];
    }
    
    area = Math.abs(area) / 2;
    
    // Конвертируем из градусов в метры (приблизительно)
    // Это упрощенный расчет, для точности нужна проекция
    const metersPerDegree = 111320; // на экваторе
    return area * metersPerDegree * metersPerDegree;
  }

  // Генерация 3D модели
  async generate3DModel(buildingParams) {
    try {
      console.log('🏗️ Starting 3D model generation:', buildingParams);
      
      // Подготавливаем данные для отправки
      const generationData = this.prepareGenerationData(buildingParams);
      
      // Симулируем процесс генерации (в реальности здесь будет API запрос)
      const result = await this.simulateGeneration(generationData);
      
      return result;
      
    } catch (error) {
      console.error('3D Generation error:', error);
      throw error;
    }
  }

  // Подготовка данных для генерации
  prepareGenerationData(params) {
    const data = {
      // Основные параметры
      geometry: params.geometry,
      height: params.height || (params.levels ? params.levels * 3.5 : 10),
      min_height: params.min_height || 0,
      base_area: params.base_area,
      
      // Материалы и стиль
      building_type: params.building_type || 'residential',
      roof_shape: params.roof_shape || 'flat',
      wall_material: params.wall_material || 'concrete',
      roof_material: params.roof_material || 'tile',
      
      // Фотографии
      photos: params.photos?.map(photo => ({
        type: photo.type,
        data: photo.file, // В реальности нужно будет загружать файлы
        filename: photo.name
      })) || [],
      
      // Метаданные
      location: {
        lat: params.coordinates.lat,
        lng: params.coordinates.lng,
        address: params.address
      },
      
      // Опции генерации
      options: {
        generate_textures: true,
        include_interior: false,
        optimize_web: true,
        high_resolution: false,
        output_format: 'glb'
      },
      
      // Источник данных
      source: params.source || 'osm',
      osm_id: params.osm_id,
      completion_percentage: params.completion_percentage || 0
    };
    
    return data;
  }

  // Симуляция процесса генерации (замена на реальный API)
  async simulateGeneration(data) {
    console.log('🎭 Simulating 3D generation process...');
    
    // Фазы генерации
    const phases = [
      { name: 'Analyzing geometry', duration: 1000 },
      { name: 'Processing photos', duration: 2000 },
      { name: 'Generating base mesh', duration: 1500 },
      { name: 'Creating textures', duration: 2500 },
      { name: 'Applying materials', duration: 1000 },
      { name: 'Optimizing model', duration: 1000 },
      { name: 'Finalizing output', duration: 500 }
    ];
    
    for (const phase of phases) {
      console.log(`🔄 ${phase.name}...`);
      await new Promise(resolve => setTimeout(resolve, phase.duration));
    }
    
    // Генерируем мок-результат
    const result = {
      success: true,
      model_id: `building_3d_${Date.now()}`,
      generation_time: phases.reduce((sum, phase) => sum + phase.duration, 0),
      
      // Файлы модели
      files: {
        glb: `building_${Date.now()}.glb`,
        preview_image: `building_${Date.now()}_preview.png`,
        metadata: `building_${Date.now()}_metadata.json`
      },
      
      // Статистика
      stats: {
        vertices: Math.floor(Math.random() * 50000) + 10000,
        faces: Math.floor(Math.random() * 100000) + 20000,
        textures: data.photos.length + 2, // photos + generated
        file_size_mb: (Math.random() * 50 + 10).toFixed(1)
      },
      
      // Качество модели
      quality: {
        geometry_accuracy: Math.min(100, 60 + data.completion_percentage * 0.4),
        texture_quality: data.photos.length > 0 ? 85 + Math.random() * 10 : 65,
        overall_score: Math.min(100, 70 + data.completion_percentage * 0.3)
      },
      
      // Интеграция
      integration: {
        web_ready: true,
        mapbox_compatible: true,
        unity_ready: data.options.high_resolution,
        threejs_ready: true
      },
      
      // Ссылки для скачивания (мок)
      download_urls: {
        glb: `/downloads/building_${Date.now()}.glb`,
        preview: `/downloads/building_${Date.now()}_preview.png`,
        package: `/downloads/building_${Date.now()}_package.zip`
      },
      
      generated_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 дней
    };
    
    console.log('✅ 3D Model generation completed:', result);
    return result;
  }

  // Получение статуса генерации
  async getGenerationStatus(modelId) {
    try {
      const response = await fetch(`${this.apiEndpoint}/3d/status/${modelId}`);
      return response.json();
    } catch (error) {
      console.error('Status check error:', error);
      return { status: 'unknown' };
    }
  }

  // Скачивание модели
  async downloadModel(downloadUrl, filename) {
    try {
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Download error:', error);
      return false;
    }
  }

  // Валидация параметров здания
  validateBuildingParams(params) {
    const errors = [];
    const warnings = [];
    
    // Обязательные параметры
    if (!params.geometry) {
      errors.push('Building geometry is required');
    }
    
    if (!params.height && !params.levels) {
      errors.push('Building height or number of levels is required');
    }
    
    if (!params.photos || params.photos.length === 0) {
      warnings.push('Photos recommended for better texture quality');
    }
    
    // Проверка размеров
    if (params.height && params.height > 500) {
      warnings.push('Very tall building detected, generation may take longer');
    }
    
    if (params.base_area && params.base_area > 100000) {
      warnings.push('Very large building detected, consider splitting into sections');
    }
    
    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  // Получение поддерживаемых форматов
  getSupportedFormats() {
    return this.supportedFormats.map(format => ({
      extension: format,
      name: format.toUpperCase(),
      description: this.getFormatDescription(format),
      web_compatible: ['glb', 'gltf'].includes(format)
    }));
  }

  getFormatDescription(format) {
    const descriptions = {
      'glb': 'Binary GLTF - Optimized for web and mobile',
      'gltf': 'GLTF JSON - Standard 3D format with external assets',
      'obj': 'Wavefront OBJ - Universal 3D format',
      'fbx': 'Autodesk FBX - Professional 3D modeling format'
    };
    
    return descriptions[format] || 'Unknown format';
  }

  // Предварительный просмотр модели (для будущего использования)
  async generatePreview(params) {
    // В реальности здесь будет генерация превью
    console.log('🖼️ Generating preview for:', params);
    
    return {
      preview_url: `/preview/building_${Date.now()}.png`,
      thumbnail_url: `/preview/building_${Date.now()}_thumb.png`
    };
  }
}

export const building3DService = new Building3DService();
export default building3DService;